// client/src/pages/WishlistPage.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import {
  Box, Typography, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, IconButton, Alert, CircularProgress,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { formatCurrency } from '../utils/currency';
import EmptyState from '../components/EmptyState';
import { API_BASE_URL } from '../utils/media';
import { useGeo } from '../context/GeoContext';

const WishlistPage = () => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token } = useContext(AuthContext);
  const { formatLocalPrice } = useGeo();

  const fetchWishlist = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/wishlist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch wishlist.');
      setWishlistItems(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchWishlist(); }, [fetchWishlist]);

  const handleRemove = async (productId) => {
    try {
      await fetch(`${API_BASE_URL}/api/wishlist/${productId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchWishlist();
    } catch (err) {
      alert('Failed to remove item.');
    }
  };

  if (loading) return <CircularProgress size={28} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>My Wishlist</Typography>
      {wishlistItems.length === 0 ? (
        <EmptyState
          icon={<FavoriteBorderIcon />}
          title="Your wishlist is empty"
          description="Tap the heart icon on any product to save it here for later."
          actionLabel="Browse Products"
          actionTo="/"
        />
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table>
            <TableHead>
              <TableRow><TableCell>Product</TableCell><TableCell align="right">Price</TableCell><TableCell align="right">Actions</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {wishlistItems.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell><Link to={`/products/${item.id}`}>{item.name}</Link></TableCell>
                  <TableCell align="right">
                    {formatCurrency(item.price)}
                    {formatLocalPrice(item.price) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        ≈ {formatLocalPrice(item.price)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton color="error" onClick={() => handleRemove(item.id)} aria-label="Remove from wishlist">
                      <DeleteOutlineIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default WishlistPage;
